"use client";

import { useEffect, useRef, useState, type MouseEvent } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import { LorenzoMark } from "./LorenzoMark";

const LIGHTING = "https://lorenzolighting.com";

/**
 * Exact hrefs from live https://lorenzolighting.com header. Off-site pages
 * are absolute so they leave this host; Projects stays `/project` (relative)
 * because that is how the lighting site itself points at this app.
 */
const NAV_LINKS = [
  { label: "Bespoke Design", href: `${LIGHTING}/bespoke/` },
  { label: "Lighting Collections", href: `${LIGHTING}/collections/` },
  { label: "Projects", href: "/project" },
  { label: "News", href: `${LIGHTING}/news/` },
  { label: "About", href: `${LIGHTING}/about/` },
  { label: "Contact", href: `${LIGHTING}/contact/` },
] as const;

function isLocalHref(href: string) {
  return href.startsWith("/") && !href.startsWith("//");
}

/**
 * Site header ported 1:1 from lorenzolighting.com (`theme.js` / `theme.css`):
 * fixed logo + primary nav on desktop, full-screen dark drawer on ≤1100px
 * driven by `.nav-toggle`, and the scroll behaviour from `initHeaderScroll`
 * (`is-scrolled` past a threshold, `is-hidden` when scrolling down again).
 * Header keeps its own dark colour from the first view (source is transparent
 * until scrolled); it still deepens + blurs on scroll.
 */
export function SiteHeader() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const openRef = useRef(false);
  openRef.current = open;

  // theme.js › initHeaderScroll
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const hero = document.querySelector<HTMLElement>("[data-hero]");
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      // Never hide the header while the drawer is open — it would clip it.
      if (openRef.current) {
        header.classList.remove("is-hidden");
        return;
      }
      const y = window.scrollY;
      const passed = hero ? y > hero.offsetHeight - 80 : y > 40;
      const goingUp = y < lastY;
      header.classList.toggle("is-scrolled", passed);
      if (!passed) header.classList.remove("is-hidden");
      else header.classList.toggle("is-hidden", !goingUp);
      lastY = y < 0 ? 0 : y;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // theme.js › initNav — scroll lock + close on Esc / desktop breakpoint / route change.
  useEffect(() => {
    document.documentElement.classList.toggle("is-nav-open", open);
    return () => document.documentElement.classList.remove("is-nav-open");
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const wide = window.matchMedia("(min-width: 1101px)");
    const onWide = (e: MediaQueryListEvent) => {
      if (e.matches) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    wide.addEventListener("change", onWide);
    return () => {
      document.removeEventListener("keydown", onKey);
      wide.removeEventListener("change", onWide);
    };
  }, [open]);

  const closeIfLink = (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest("a")) setOpen(false);
  };

  return (
    <header ref={headerRef} className="site-header">
      <div className="lz-container">
        <a href={`${LIGHTING}/`} className="brand" aria-label="Lorenzo">
          <span className="brand__mark">
            <LorenzoMark />
          </span>
        </a>

        <nav
          className={clsx("nav", open && "is-open")}
          aria-label="Primary"
          onClick={closeIfLink}
        >
          <ul className="nav__list">
            {NAV_LINKS.map((l) => {
              const match = l.href === "/project" ? "/projects" : l.href;
              const current =
                isLocalHref(match) &&
                (pathname === match || pathname.startsWith(`${match}/`));
              return (
                <li key={l.href} className={clsx(current && "is-current")}>
                  {isLocalHref(l.href) ? (
                    <Link href={l.href} aria-current={current ? "page" : undefined}>
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.href}>{l.label}</a>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <button
          type="button"
          className="nav-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <svg
            className="nav-toggle__open"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <svg
            className="nav-toggle__close"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </header>
  );
}
